/**
 * RoleSetEditor — the unified, role-primary grant surface for ONE selected user.
 *
 * Replaces the legacy binary admin/user toggle. An admin picks a SET of ROLES
 * (multi-select checkboxes grouped by surface: Platform / Chat / CRM / Expense,
 * sourced from the vendored rbac-model). The user's AUTHORITIES render read-only
 * as the de-duplicated UNION of every selected role's authorities — you grant
 * roles, the authority list is derived, never directly editable (default-DENY:
 * no roles ⇒ no authority). Save PATCHes /api/admin/users { user_id, roles[] }.
 *
 * The "Advanced — per-permission overrides" expander is intentionally a stub
 * this pass (role-primary was the approved model; per-permission overrides are a
 * deferred follow-on). It renders a disabled "coming soon" affordance so the
 * affordance exists without implying it works.
 *
 * Mutation UX matches the rest of admin: optimistic cache write of the new role
 * set (badge + editor reflect immediately), sonner toast on success, rollback +
 * error toast on failure. All traffic flows through the data-access hook →
 * vendored `api` wrapper → the single auth seam (no per-component fetch/MSAL).
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@andavo/web-ui';
import { useSetUserRoles } from '../data-access/useAdmin';
import {
  deriveAuthorities,
  RBAC_SURFACES,
  rolesForSurface,
  storedRolesFromAdminUser,
  type SurfaceKey,
} from '../lib/rbac-model';
import type { AdminUser } from '../types';
import { AdminError, Panel } from './primitives';

/** Stable, order-independent equality for two stored-role string sets. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

export function RoleSetEditor({ user }: { user: AdminUser }) {
  const baseline = useMemo(() => storedRolesFromAdminUser(user), [user]);
  const [selected, setSelected] = useState<string[]>(baseline);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const setRoles = useSetUserRoles();

  // Re-seed when the selected user (or their authoritative set) changes — e.g.
  // the admin looks up a different account, or a refetch returns a new set.
  // Keying on the user id + the baseline contents keeps a fresh-but-equal
  // refetch from clobbering in-flight edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: id+baseline identity is the intended reseed trigger
  useEffect(() => {
    setSelected(baseline);
  }, [user.id, baseline]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const derived = useMemo(() => deriveAuthorities(selected), [selected]);
  const dirty = !sameSet(selected, baseline);

  const toggleRole = (stored: string) => {
    setSelected((prev) =>
      prev.includes(stored)
        ? prev.filter((r) => r !== stored)
        : [...prev, stored],
    );
  };

  const handleSave = () => {
    // Send a stable, de-duplicated set (defensive — toggles already prevent dups).
    const roles = [...new Set(selected)];
    setRoles.mutate(
      // baseline_roles makes the server apply this as a diff against what is
      // stored NOW, so a role another admin changed meanwhile is left alone.
      { user_id: user.id, roles, baseline_roles: baseline },
      {
        onSuccess: () =>
          toast.success(
            roles.length === 0
              ? `Removed all roles from ${user.email}.`
              : `Saved ${roles.length} role${roles.length === 1 ? '' : 's'} for ${user.email}.`,
          ),
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Failed to save roles'),
      },
    );
  };

  const handleReset = () => setSelected(baseline);

  return (
    <Panel
      title={`Roles — ${user.email}`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            data-testid="role-set-reset"
            disabled={!dirty || setRoles.isPending}
            onClick={handleReset}
            size="sm"
            type="button"
            variant="secondary"
          >
            Reset
          </Button>
          <Button
            data-testid="role-set-save"
            disabled={!dirty || setRoles.isPending}
            onClick={handleSave}
            size="sm"
            type="button"
          >
            {setRoles.isPending ? 'Saving…' : 'Save roles'}
          </Button>
        </div>
      }
    >
      <div data-testid="role-set-editor" data-user-id={user.id}>
        {setRoles.isError ? (
          <div className="mb-3">
            <AdminError
              message={`Save failed: ${(setRoles.error as Error)?.message ?? 'unknown'}`}
            />
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          {RBAC_SURFACES.map((surface) => (
            <SurfaceGroup
              key={surface.key}
              surface={surface.key}
              label={surface.label}
              description={surface.description}
              selected={selectedSet}
              disabled={setRoles.isPending}
              onToggle={toggleRole}
            />
          ))}
        </div>

        <DerivedAuthorities authorities={derived} />

        <AdvancedOverrides
          open={advancedOpen}
          onToggle={() => setAdvancedOpen((o) => !o)}
        />
      </div>
    </Panel>
  );
}

function SurfaceGroup({
  surface,
  label,
  description,
  selected,
  disabled,
  onToggle,
}: {
  surface: SurfaceKey;
  label: string;
  description: string;
  selected: Set<string>;
  disabled: boolean;
  onToggle: (stored: string) => void;
}) {
  const roles = rolesForSurface(surface);
  return (
    <fieldset
      className="rounded-md border border-border-subtle p-3"
      data-testid={`role-surface-${surface}`}
    >
      <legend className="px-1 font-sans font-medium text-content-primary text-sm">
        {label}
      </legend>
      <p className="mb-2 font-inter text-content-tertiary text-xs">
        {description}
      </p>
      <div className="flex flex-col gap-1.5">
        {roles.map((role) => (
          <label
            className="flex cursor-pointer items-start gap-2 font-inter text-content-secondary text-sm"
            key={role.stored}
          >
            <input
              checked={selected.has(role.stored)}
              className="mt-0.5 size-4 shrink-0 rounded border-border-subtle accent-content-action"
              data-testid={`role-checkbox-${role.stored}`}
              disabled={disabled}
              onChange={() => onToggle(role.stored)}
              type="checkbox"
            />
            <span>
              <span className="font-medium text-content-primary">
                {role.label}
              </span>
              <span className="ml-1 font-mono text-content-tertiary text-xs">
                {role.stored}
              </span>
              <span className="block text-content-tertiary text-xs">
                {role.description}
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function DerivedAuthorities({
  authorities,
}: {
  authorities: ReturnType<typeof deriveAuthorities>;
}) {
  return (
    <div
      className="mt-4 rounded-md border border-border-subtle bg-surface-background p-3"
      data-testid="derived-authorities"
    >
      <h3 className="mb-1 font-sans font-medium text-content-primary text-sm">
        Effective authorities{' '}
        <span className="font-inter font-normal text-content-tertiary text-xs">
          (derived — the union of the selected roles; read-only)
        </span>
      </h3>
      {authorities.length === 0 ? (
        <p
          className="font-inter text-content-tertiary text-xs"
          data-testid="derived-authorities-empty"
        >
          No roles selected — this user has no access on any surface
          (default-deny).
        </p>
      ) : (
        <ul
          className="flex flex-wrap gap-1.5"
          data-testid="derived-authorities-list"
        >
          {authorities.map((a) => (
            <li
              className="rounded-full bg-content-tertiary/15 px-2 py-0.5 font-mono text-content-secondary text-xs"
              data-authority={a.key}
              data-testid="derived-authority"
              key={a.key}
              title={a.label}
            >
              {a.key}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Per-permission overrides — DEFERRED. Role-primary was the approved model;
 * direct per-permission grants are a tracked follow-on. We render the expander
 * (so the affordance is discoverable) but its body is an explicit "coming soon"
 * with no editable controls — nothing here mutates the payload.
 */
function AdvancedOverrides({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mt-4 border-border-subtle border-t pt-3">
      <button
        aria-expanded={open}
        className="flex items-center gap-1.5 font-inter text-content-secondary text-sm hover:text-content-primary"
        data-testid="advanced-overrides-toggle"
        onClick={onToggle}
        type="button"
      >
        <span aria-hidden className="text-content-tertiary text-xs">
          {open ? '▾' : '▸'}
        </span>
        Advanced — per-permission overrides
        <span className="ml-1 rounded-full bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 text-xs">
          coming soon
        </span>
      </button>
      {open ? (
        <p
          className="mt-2 font-inter text-content-tertiary text-xs"
          data-testid="advanced-overrides-body"
        >
          Per-permission overrides are not available yet. Access is granted by
          ROLE (above); the effective authorities are derived from the selected
          roles. Direct per-permission grants are a tracked follow-on.
        </p>
      ) : null}
    </div>
  );
}

export default RoleSetEditor;
